import { Avatar, Box, Chip, InputAdornment, Paper } from "@mui/material";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { ReactComponent as CloseIconSmall } from "assets/CloseIconSmall.svg";
import { ReactComponent as UserCircle } from "assets/UserCircle.svg";
import { clearUserDetail, fetchAutoCompleteAsync } from "components/features/MyPlanDetails/MyPlanDetails.slice";
import { sanitize } from "dompurify";
import { debounce, isEmpty, isUndefined } from "lodash";
import { useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "redux/hooks";
import { tokens } from "styles/Tokens";
import { getInitials } from "utils/CommonFunctions";
import { getResolvedUserDisplayName } from "utils/SharedUtils";
import { PAGE_SIZE, messages, minSearchStrLength } from "../../utils/CommonConstants";
import Typography from "./Typography";
import Validation from "./Validation";

interface Props {
  name?: string;
  getUserDetail: (value: any) => void;
  assigneeList?: any;
  assignedMember?: any;
  placeholder?: string;
  selectedInputValue?: string;
  onBlur?: (args?: any) => void;
  autoFocus?: boolean;
  multiple?: boolean;
  showIcon?: boolean;
  searchParams?: any;
  disabled?: boolean;
  isUpdateListType?: boolean;
  size?: "small" | "medium";
  error?: any;
  onChange?: (v: any) => void;
  isCurrentMemberExcluded?: boolean;
  excludedMember?: string[] | [];
  getUserInfo?: (val: any) => void;
  includecorpusers?: boolean;
}

const CustomPaper = (props: any) => {
  return (
    <Paper
      sx={{
        width: "100%",

        boxShadow: "0px 10px 25px rgba(100, 116, 139, 0.1), 0px 20px 40px 8px rgba(100, 116, 139, 0.3);",
        "& .MuiAutocomplete-listbox": {
          "& .MuiAutocomplete-option[aria-selected='true']": {
            bgcolor: tokens.Background.General.Color,
            "&.Mui-focused": {
              bgcolor: tokens.Background.General.Color,
              color: `${tokens.Text.General.Color}`
            }
          }
        },
        "& .MuiAutocomplete-listbox .MuiAutocomplete-option.Mui-focused": {
          bgcolor: tokens.Background.General.Color,
          color: `${tokens.Text.General.Color}`
        },
        ".MuiAutocomplete-noOptions": {
          display: "none"
        }
      }}
      {...props}
    />
  );
};

export default function AsyncAutocomplete(props: Props) {
  const {
    getUserDetail,
    assigneeList,
    isUpdateListType,
    assignedMember,
    placeholder,
    onBlur,
    autoFocus = false,
    multiple = true,
    showIcon = true,
    searchParams,
    disabled = false,
    selectedInputValue = "",
    size = "medium",
    name,
    error,
    onChange,
    isCurrentMemberExcluded = false,
    excludedMember = [],
    getUserInfo,
    includecorpusers
  } = props;
  const [value, setValue] = useState([]);
  const [inputValue, setInputValue] = useState("");
  const [isOnFocus, setIsOnFocus] = useState(false);
  const dispatch = useAppDispatch();
  let userList: any = useAppSelector((state) => state.myPlanDetails.userDetail);
  let isUserDetailLoading: any = useAppSelector((state) => state.myPlanDetails.isUserDetailLoading);
  let [pageNo] = useState(0);
  let data: any;
  if (assignedMember?.length) {
    data = assignedMember;
  }
  if (assigneeList) {
    data = assigneeList ? value : [...assignedMember, ...value];
  }
  if (data?.length > 0) {
    userList = userList?.filter((usr: any) => {
      return !data?.some((assig: any) => {
        return assig.hltId === usr.hltId;
      });
    });
  }

  useEffect(() => {
    if (inputValue.length === 0 && userList.length && !isUpdateListType) dispatch(clearUserDetail());
  }, [userList]);

  useEffect(() => {
    if (isUpdateListType) {
      if (selectedInputValue === null) setInputValue("");
      else setInputValue(selectedInputValue);
    }
  }, [selectedInputValue, disabled]);

  useEffect(() => {
    if (assigneeList?.length) {
      onChange?.(assigneeList);
      setValue(assigneeList);
    }
    if (!multiple && !assigneeList?.length) {
      setValue([]);
      setInputValue("");
    }
  }, [assigneeList]);

  const loadData = (inputValue: any, params: any) => {
    let data = {
      searchText: inputValue,
      page: pageNo,
      size: PAGE_SIZE,
      ...params
    };
    if (!isUndefined(includecorpusers)) data.includecorpusers = includecorpusers;
    dispatch(fetchAutoCompleteAsync(data));
  };

  const debouncedLoadData = useRef(debounce(loadData, 500));

  const handleInputChange = (e: any, v: any, reason: string) => {
    setInputValue(v);
    if (reason === "input") {
      if (v.length > minSearchStrLength) {
        debouncedLoadData.current(v, searchParams);
      }
    }

    if (v.trim().length === 0) {
      setInputValue("");
      dispatch(clearUserDetail());
    }
  };

  const getHiltnId = (users: any) => {
    if (multiple) {
      return users.map((user: any) => user.hltId);
    }

    return users?.hltId ? [users?.hltId] : [];
  };

  const handleChange = (e: any, v: any) => {
    setValue(v);
    onChange?.(v);
    isUpdateListType ? getUserDetail(v) : getUserDetail(getHiltnId(v));
    getUserInfo && getUserInfo(v);
  };

  const filterOptions = createFilterOptions({
    stringify: (user: any) => user.firstName + user.lastName + user.displayName + user.email
  });

  const onFocus = () => {
    setIsOnFocus(true);
  };

  const excludedUser = () => {
    if (excludedMember) return userList.filter((item: any) => !excludedMember.includes(item.hltId));
    return userList;
  };

  const userOptions = excludedUser();

  return (
    <>
      <Autocomplete
        size={size}
        sx={{
          "&": {
            display: "flex",
            ".MuiOutlinedInput-notchedOutline": {
              border: `1px solid ${tokens.Border.General.Tertiary}`,
              borderRadius: "8px"
            },

            ".MuiAutocomplete-popupIndicator": {
              display: "none"
            }
          },
          "& .MuiAutocomplete-inputRoot .MuiAutocomplete-input": {
            width: value?.length ? "100%" : "0"
          },
          "&.MuiAutocomplete-hasPopupIcon.MuiAutocomplete-hasClearIcon .MuiOutlinedInput-root": {
            paddingRight: "9px"
          }
        }}
        disabled={disabled}
        popupIcon=""
        multiple={multiple}
        freeSolo={multiple === false}
        value={value}
        PaperComponent={CustomPaper}
        inputValue={inputValue || ""}
        ListboxProps={{
          style: {
            padding: 0,
            alignSelf: "center"
          }
        }}
        onChange={handleChange}
        onInputChange={(e, value, reason) => handleInputChange(e, value, reason)}
        filterOptions={filterOptions}
        disablePortal
        openOnFocus
        options={userOptions}
        getOptionLabel={(user: any) =>
          user?.displayName
            ? `${user.displayName}`
            : `${user?.firstName ? user.firstName : ""} ${user?.lastName ? user.lastName : ""}`
        }
        isOptionEqualToValue={(user: any, value: any) => user.hltId === value.hltId}
        id="aysnc-auto-complete"
        renderInput={(params) => {
          return (
            <TextField
              {...params}
              sx={{
                "& input::placeholder": {
                  color: tokens.Text.General.Tertiary,
                  opacity: 0.5
                }
              }}
              placeholder={placeholder}
              id="outlined-basic"
              onBlur={() => {
                setIsOnFocus(false);
                onBlur?.();
              }}
              onFocus={onFocus}
              autoFocus={autoFocus}
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    {showIcon && (
                      <InputAdornment sx={{ paddingLeft: "6px" }} position="start">
                        <UserCircle />
                      </InputAdornment>
                    )}
                    {params.InputProps.startAdornment}
                  </>
                )
              }}
            />
          );
        }}
        renderOption={(props, user: any, { inputValue }) => {
          const initials = getInitials(user);
          const displaytext = `${
            user?.displayName
              ? `${user.displayName} ${user?.email ? "(" + user.email + ")" : ""}`
              : `${user?.firstName ? user.firstName : ""} ${user?.lastName ? user.lastName : ""} ${
                  user?.email ? "(" + user.email + ")" : ""
                }`
          }`;
          let regExp = new RegExp(inputValue, "g");
          const alteredText = displaytext.replace(regExp, `<b>${inputValue}</b>`);

          return (
            <li {...props}>
              <Box display={"flex"} flexDirection={"row"} sx={{ alignItems: "center", minHeight: "37px" }}>
                <Avatar
                  src={user?.src}
                  sx={{
                    fontSize: "12px",
                    border: `1px solid ${tokens.Border.General.Primary}`,
                    height: "24px",
                    width: "24px",
                    background: "unset",
                    marginRight: "10px",
                    color: `${tokens.Text.General.Secondary}`
                  }}
                >
                  <Typography variant="label" showBold>
                    {initials}
                  </Typography>
                </Avatar>
                <Typography variant="body2" sx={{ textAlign: "left" }}>
                  <span dangerouslySetInnerHTML={{ __html: sanitize(alteredText) }}></span>
                </Typography>
              </Box>
            </li>
          );
        }}
        renderTags={(value, getTagProps) =>
          value.map((user, index) => {
            const resolvedName = getResolvedUserDisplayName(user);
            const isUnresolved = isEmpty(resolvedName);

            return (
              <Chip
                sx={{
                  "&": { borderRadius: "8px" },
                  "& .MuiChip-deleteIcon": {
                    marginRight: "8px"
                  }
                }}
                deleteIcon={<CloseIconSmall />}
                avatar={
                  <Avatar
                    src={user?.src}
                    sx={{
                      border: `1px solid ${tokens.Text.General.Color}`,
                      height: "24px",
                      width: "24px",
                      background: "unset",
                      marginLeft: "-10px",
                      marginRight: "10px",
                      "&.MuiChip-avatar": {
                        color: `${tokens.Text.General.Color} !important`
                      }
                    }}
                  >
                    <Typography variant="label" showBold color={isUnresolved ? tokens.Text.Feedback.Alert : ""}>
                      {getInitials(user)}
                    </Typography>
                  </Avatar>
                }
                variant="outlined"
                label={
                  !isUnresolved ? (
                    resolvedName
                  ) : (
                    <Box component="span" color={tokens.Text.Feedback.Alert}>
                      {user?.hltId}
                    </Box>
                  )
                }
                {...getTagProps({ index })}
              />
            );
          })
        }
      />
      {inputValue.length > minSearchStrLength && (
        <Typography variant={"label"}>
          {isUserDetailLoading && isOnFocus ? "Loading..." : isEmpty(userList) && isOnFocus && messages.userNotFound}
        </Typography>
      )}
      {error && <Validation name={name} type={error.type} message={error.message} />}
    </>
  );
}
